import API from '../config/api';
import React, { useEffect, useState } from 'react';

import axios from 'axios';
import { toast } from 'react-toastify';

import { Container, Row, Col, Card, Form } from 'react-bootstrap';

import CustomNavbar from '../components/CustomNavbar';

import { confirmToast } from '../utils/toast';

/* Avatar palettes, picked by hashing the user's id rather than their position
   in the list — so a person keeps the same colour once you start filtering. */
const AVATAR_TONES = [
  { from: '#c2410c', to: '#f59e0b', solid: '#ea580c' },
  { from: '#5b21b6', to: '#a855f7', solid: '#7c3aed' },
  { from: '#0e7490', to: '#22d3ee', solid: '#0891b2' },
  { from: '#15803d', to: '#4ade80', solid: '#16a34a' },
  { from: '#9f1239', to: '#fb7185', solid: '#e11d48' },
  { from: '#b45309', to: '#fcd34d', solid: '#d97706' }
];

const toneFor = (id = '') => {
  let hash = 0;

  for (let i = 0; i < id.length; i += 1) {
    hash = (hash * 31 + id.charCodeAt(i)) >>> 0;
  }

  return AVATAR_TONES[hash % AVATAR_TONES.length];
};

function AdminUsersPage() {
  const [users, setUsers] = useState([]);

  const [search, setSearch] = useState('');

  const [roleFilter, setRoleFilter] = useState('');

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const { data } = await axios.get(`${API}/users`);

      setUsers(data);
    } catch (error) {
      console.log(error);
    }
  };

  const deleteUser = async (user) => {
    /* Deleting an account cannot be undone — the other admin screens all
       confirm first, and this one should too. */
    if (!(await confirmToast(`Delete ${user.name || 'this user'}?`))) {
      return;
    }

    try {
      await axios.delete(`${API}/users/${user._id}`);

      toast.success('User Deleted');

      fetchUsers();
    } catch (error) {
      console.log(error);
    }
  };

  const makeAdmin = async (user) => {
    /* Handing out admin rights deserves the same pause */
    if (!(await confirmToast(`Promote ${user.name || 'this user'} to admin?`))) {
      return;
    }

    try {
      await axios.put(`${API}/users/admin/${user._id}`);

      toast.success('User Promoted To Admin');

      fetchUsers();
    } catch (error) {
      console.log(error);
    }
  };

  const filteredUsers = users.filter((user) => {
    const keyword = search.toLowerCase();

    const matchesSearch =
      user.name?.toLowerCase().includes(keyword) ||
      user.email?.toLowerCase().includes(keyword) ||
      user.phone?.toLowerCase().includes(keyword);

    const matchesRole =
      !roleFilter ||
      (roleFilter === 'admin' ? user.isAdmin : !user.isAdmin);

    return matchesSearch && matchesRole;
  });

  const adminCount = users.filter((u) => u.isAdmin).length;
  const customerCount = users.length - adminCount;

  const roleChips = [
    { key: '', label: '✨ Everyone', count: users.length, color: '#9a3412' },
    { key: 'admin', label: '⭐ Admins', count: adminCount, color: '#b45309' },
    {
      key: 'user',
      label: '🙏 Customers',
      count: customerCount,
      color: '#7c3aed'
    }
  ];

  return (
    <>
      <CustomNavbar />

      <div className="admin-users-wrap">
        <Container>
          {/* Header */}
          <Card className="shadow border-0 mb-4 au-hero">
            <span className="au-hero-aurora" aria-hidden="true" />

            {/* Motes drifting up through the banner */}
            <span className="au-motes" aria-hidden="true">
              {Array.from({ length: 8 }).map((_, i) => (
                <span className="au-mote" key={i} />
              ))}
            </span>

            <Card.Body className="position-relative p-4 d-flex flex-wrap justify-content-between align-items-center gap-3">
              <div>
                <h1 className="fw-bold mb-1 au-hero-title">
                  <span className="au-hero-icon">👥</span>
                  <span className="au-hero-title-text">User Management</span>
                </h1>
                <p className="mb-0 au-hero-text">Manage all registered users</p>
              </div>

              <div className="au-stats">
                <div className="au-stat">
                  <span className="au-stat-value">{users.length}</span>
                  <span className="au-stat-label">Total</span>
                </div>

                <div className="au-stat is-admin">
                  <span className="au-stat-value">{adminCount}</span>
                  <span className="au-stat-label">Admins</span>
                </div>

                <div className="au-stat is-user">
                  <span className="au-stat-value">{customerCount}</span>
                  <span className="au-stat-label">Customers</span>
                </div>
              </div>
            </Card.Body>
          </Card>

          {/* Search */}
          <div className="au-search-row">
            <div className="au-search">
              <span className="au-search-icon" aria-hidden="true">
                🔍
              </span>

              <Form.Control
                className="au-search-input"
                placeholder="Search users by name, email or phone..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />

              {search && (
                <button
                  type="button"
                  className="au-search-clear"
                  aria-label="Clear search"
                  onClick={() => setSearch('')}
                >
                  ×
                </button>
              )}
            </div>

            <span className="au-result-count">
              <span className="au-result-num">{filteredUsers.length}</span>
              shown
            </span>
          </div>

          {/* Role chips */}
          <div className="au-chips">
            {roleChips.map((chip) => (
              <button
                key={chip.key || 'all'}
                type="button"
                className={`au-chip${roleFilter === chip.key ? ' is-active' : ''}`}
                style={{ '--chip': chip.color }}
                onClick={() => setRoleFilter(chip.key)}
              >
                {chip.label}
                <span className="au-chip-count">{chip.count}</span>
              </button>
            ))}
          </div>

          {/* User cards */}
          {filteredUsers.length === 0 ? (
            <Card
              className="border-0 shadow-sm text-center py-5"
              style={{ borderRadius: '18px' }}
            >
              <Card.Body>
                <div className="au-empty-icon">🪔</div>
                <h5 className="fw-bold mt-2">No Users Found</h5>
                <p className="text-muted mb-0">Try a different search term.</p>
              </Card.Body>
            </Card>
          ) : (
            <Row className="g-4">
              {filteredUsers.map((user, index) => {
                const tone = toneFor(user._id || user.email || '');

                return (
                  <Col xl={4} lg={6} md={6} xs={12} key={user._id}>
                    <Card
                      className={`border-0 shadow-sm h-100 au-card${
                        user.isAdmin ? ' is-admin' : ''
                      }`}
                      style={{
                        '--u-from': tone.from,
                        '--u-to': tone.to,
                        '--u-accent': tone.solid,
                        /* capped so late cards don't wait seconds */
                        '--i': Math.min(index, 11)
                      }}
                    >
                      <span className="au-card-strip" aria-hidden="true" />

                      <Card.Body className="p-4">
                        <div className="d-flex align-items-center gap-3 mb-3">
                          <span className="au-avatar-wrap">
                            {/* Admins get a halo turning behind them */}
                            {user.isAdmin && (
                              <span className="au-halo" aria-hidden="true" />
                            )}

                            <span className="au-avatar">
                              {user.name?.charAt(0).toUpperCase() || '?'}
                            </span>

                            {user.isAdmin && (
                              <span className="au-crown" aria-hidden="true">
                                👑
                              </span>
                            )}
                          </span>

                          <div className="flex-grow-1 text-truncate">
                            <div className="fw-bold text-truncate au-name">
                              {user.name}
                            </div>

                            {user.isAdmin ? (
                              <span className="au-role is-admin">
                                <span className="au-role-dot" />⭐ Admin
                              </span>
                            ) : (
                              <span className="au-role is-user">
                                <span className="au-role-dot" />
                                Customer
                              </span>
                            )}
                          </div>
                        </div>

                        <div className="au-info">
                          <div className="au-info-row text-truncate">
                            <span className="au-info-icon">✉️</span>
                            <span className="text-truncate">{user.email}</span>
                          </div>
                          <div className="au-info-row">
                            <span className="au-info-icon">📞</span>
                            <span>{user.phone || 'N/A'}</span>
                          </div>
                        </div>

                        <div className="d-flex gap-2 mt-3">
                          {!user.isAdmin && (
                            <button
                              type="button"
                              className="au-act au-act-promote flex-fill"
                              onClick={() => makeAdmin(user)}
                            >
                              <span className="au-act-ico" aria-hidden="true">
                                ⭐
                              </span>
                              Make Admin
                            </button>
                          )}

                          <button
                            type="button"
                            className="au-act au-act-del flex-fill"
                            onClick={() => deleteUser(user)}
                          >
                            <span className="au-act-ico" aria-hidden="true">
                              🗑
                            </span>
                            Delete
                          </button>
                        </div>
                      </Card.Body>
                    </Card>
                  </Col>
                );
              })}
            </Row>
          )}
        </Container>
      </div>

      <style>{adminUsersStyles}</style>
    </>
  );
}

const adminUsersStyles = `
  .admin-users-wrap {
    background: radial-gradient(720px 320px at 8% -5%, rgba(124,58,237,0.12), transparent 70%), radial-gradient(720px 320px at 92% 105%, rgba(234,88,12,0.14), transparent 70%), linear-gradient(180deg, #faf5ff 0%, #fff7ed 55%, #fef3c7 100%);
    min-height: 100vh;
    padding: clamp(24px, 4vw, 40px) 0;
  }

  /* ===== Sanctum banner ===== */
  .au-hero {
    position: relative;
    overflow: hidden;
    isolation: isolate;
    border-radius: 22px;
    color: #fff;
    background: linear-gradient(120deg, #0f172a, #3b0764 30%, #7c2d12 64%, #ea580c);
    background-size: 220% 220%;
    border: 1px solid rgba(253, 230, 138, 0.25) !important;
    animation: auHeroDrift 18s ease-in-out infinite alternate;
  }
  @keyframes auHeroDrift {
    from { background-position: 0% 50%; }
    to   { background-position: 100% 50%; }
  }
  .au-hero::before,
  .au-hero::after {
    content: '';
    position: absolute;
    left: 0; right: 0;
    height: 2px;
    z-index: 3;
    background: linear-gradient(90deg, transparent, #fde68a 20%, #f59e0b 50%, #fde68a 80%, transparent);
    background-size: 220% 100%;
    animation: auThread 3.6s linear infinite;
  }
  .au-hero::before { top: 0; }
  .au-hero::after { bottom: 0; animation-direction: reverse; }
  @keyframes auThread {
    to { background-position: 220% 0; }
  }

  .au-hero-aurora {
    position: absolute;
    inset: -40%;
    z-index: 0;
    pointer-events: none;
    background:
      radial-gradient(36% 44% at 24% 30%, rgba(251,191,36,0.40), transparent 62%),
      radial-gradient(32% 40% at 78% 26%, rgba(168,85,247,0.34), transparent 62%),
      radial-gradient(40% 46% at 58% 84%, rgba(234,88,12,0.38), transparent 62%);
    filter: blur(30px);
    animation: auAurora 22s ease-in-out infinite alternate;
    will-change: transform;
  }
  @keyframes auAurora {
    0%   { transform: translate3d(-4%, -3%, 0) rotate(0deg)  scale(1.05); }
    50%  { transform: translate3d(5%, 4%, 0)   rotate(8deg)  scale(1.18); }
    100% { transform: translate3d(-3%, 5%, 0)  rotate(-6deg) scale(1.1); }
  }

  .au-motes { position: absolute; inset: 0; z-index: 1; pointer-events: none; overflow: hidden; }
  .au-mote {
    position: absolute;
    bottom: -12px;
    width: 6px; height: 6px;
    border-radius: 50%;
    background: rgba(253, 230, 138, 0.9);
    box-shadow: 0 0 12px rgba(251, 191, 36, 0.9);
    opacity: 0;
    animation: auMote 12s linear infinite;
  }
  .au-mote:nth-child(1) { left: 7%;  animation-delay: 0s; }
  .au-mote:nth-child(2) { left: 20%; animation-delay: 4.3s; width: 4px; height: 4px; }
  .au-mote:nth-child(3) { left: 33%; animation-delay: 1.7s; }
  .au-mote:nth-child(4) { left: 46%; animation-delay: 7.2s; width: 5px; height: 5px; }
  .au-mote:nth-child(5) { left: 59%; animation-delay: 2.8s; }
  .au-mote:nth-child(6) { left: 72%; animation-delay: 9.2s; width: 4px; height: 4px; }
  .au-mote:nth-child(7) { left: 85%; animation-delay: 5.6s; }
  .au-mote:nth-child(8) { left: 95%; animation-delay: 3.4s; width: 5px; height: 5px; }
  @keyframes auMote {
    0%   { opacity: 0; transform: translateY(0) translateX(0) scale(0.6); }
    12%  { opacity: 1; }
    80%  { opacity: 0.6; }
    100% { opacity: 0; transform: translateY(-190px) translateX(22px) scale(1.1); }
  }

  .au-hero .card-body { z-index: 2; }
  .au-hero-title { display: flex; align-items: center; gap: 10px; }
  .au-hero-icon {
    display: inline-block;
    animation: auIconBob 3.2s ease-in-out infinite;
  }
  @keyframes auIconBob {
    0%, 100% { transform: translateY(0) rotate(0deg); }
    50%      { transform: translateY(-6px) rotate(-7deg); }
  }
  .au-hero-title-text {
    background: linear-gradient(100deg,
      #ffffff 0%, #ffffff 32%, #fde68a 46%, #fbbf24 52%, #fde68a 58%, #ffffff 74%, #ffffff 100%);
    background-size: 250% 100%;
    -webkit-background-clip: text;
    background-clip: text;
    -webkit-text-fill-color: transparent;
    color: transparent;
    animation: auTitleShine 5.5s ease-in-out infinite;
  }
  @keyframes auTitleShine {
    0%, 100% { background-position: 130% 0; }
    50%      { background-position: -30% 0; }
  }
  .au-hero-text { opacity: 0.85; }

  .au-stats { display: flex; flex-wrap: wrap; gap: 9px; }
  .au-stat {
    display: inline-flex;
    flex-direction: column;
    align-items: center;
    min-width: 84px;
    padding: 9px 16px;
    border-radius: 16px;
    background: rgba(255, 255, 255, 0.12);
    border: 1px solid rgba(253, 230, 138, 0.3);
    backdrop-filter: blur(6px);
    transition: transform 0.25s cubic-bezier(0.34, 1.56, 0.64, 1),
      background 0.25s ease, border-color 0.25s ease;
  }
  .au-stat:hover {
    transform: translateY(-3px);
    background: rgba(255, 255, 255, 0.2);
    border-color: rgba(253, 230, 138, 0.65);
  }
  .au-stat-value { font-size: 1.35rem; font-weight: 800; line-height: 1.1; }
  .au-stat-label {
    font-size: 0.64rem;
    text-transform: uppercase;
    letter-spacing: 1px;
    opacity: 0.78;
    margin-top: 2px;
  }
  .au-stat.is-admin .au-stat-value { color: #fde68a; }
  .au-stat.is-user  .au-stat-value { color: #d8b4fe; }

  /* ===== Search ===== */
  .au-search-row {
    display: flex;
    align-items: center;
    gap: 14px;
    flex-wrap: wrap;
    margin-bottom: 16px;
  }
  .au-search {
    position: relative;
    display: flex;
    align-items: center;
    gap: 10px;
    flex: 1 1 320px;
    max-width: 480px;
    padding: 4px 14px;
    border-radius: 50px;
    background: #fff;
    border: 2px solid rgba(245, 158, 11, 0.26);
    box-shadow: 0 10px 28px rgba(194, 65, 12, 0.1);
    transition: border-color 0.25s ease, box-shadow 0.25s ease;
  }
  .au-search:focus-within {
    border-color: #f59e0b;
    box-shadow: 0 14px 36px rgba(245, 158, 11, 0.28);
  }
  .au-search-icon { flex: none; font-size: 1rem; }
  .au-search-input {
    border: none !important;
    padding: 9px 0;
    box-shadow: none !important;
    background: transparent;
  }
  .au-search-clear {
    flex: none;
    border: none;
    width: 22px; height: 22px;
    border-radius: 50%;
    line-height: 1;
    font-size: 1.05rem;
    color: #b45309;
    background: #fff7ed;
    cursor: pointer;
    transition: background 0.2s ease, transform 0.2s ease;
  }
  .au-search-clear:hover { background: #fde68a; transform: rotate(90deg); }
  .au-result-count {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    font-size: 0.8rem;
    font-weight: 600;
    color: #9a3412;
  }
  .au-result-num {
    font-weight: 800;
    color: #fff;
    background: linear-gradient(135deg, #ea580c, #f59e0b);
    border-radius: 30px;
    padding: 2px 11px;
    box-shadow: 0 4px 12px rgba(234, 88, 12, 0.3);
  }

  /* ===== Role chips ===== */
  .au-chips { display: flex; flex-wrap: wrap; gap: 9px; margin-bottom: 22px; }
  .au-chip {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 7px 9px 7px 15px;
    border-radius: 50px;
    font-size: 0.82rem;
    font-weight: 700;
    cursor: pointer;
    background: rgba(255, 255, 255, 0.75);
    border: 1px solid var(--chip, #f59e0b);
    color: var(--chip, #9a3412);
    transition: transform 0.25s cubic-bezier(0.34, 1.56, 0.64, 1),
      background 0.25s ease, color 0.25s ease, box-shadow 0.25s ease;
  }
  .au-chip:hover { transform: translateY(-2px); }
  .au-chip:active { transform: translateY(0) scale(0.96); }
  .au-chip.is-active {
    color: #fff;
    background: var(--chip, #ea580c);
    animation: auChipPop 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
  }
  @keyframes auChipPop {
    0%   { transform: scale(0.88); }
    55%  { transform: scale(1.08); }
    100% { transform: scale(1); }
  }
  .au-chip-count {
    min-width: 22px;
    padding: 1px 8px;
    border-radius: 30px;
    font-size: 0.72rem;
    font-weight: 800;
    background: #fff7ed;
    color: var(--chip, #9a3412);
  }
  .au-chip.is-active .au-chip-count { background: rgba(255, 255, 255, 0.92); }

  /* ===== User cards ===== */
  .au-card {
    position: relative;
    overflow: hidden;
    border-radius: 20px;
    border: 1px solid rgba(245, 158, 11, 0.14) !important;
    transition: transform 0.25s ease, box-shadow 0.25s ease;
    animation: auCardIn 0.5s cubic-bezier(0.22, 1, 0.36, 1) backwards;
    animation-delay: calc(var(--i, 0) * 60ms);
  }
  @keyframes auCardIn {
    from { opacity: 0; transform: translateY(20px) scale(0.96); }
    to   { opacity: 1; transform: translateY(0) scale(1); }
  }
  /* Top strip in the person's own colour, with light travelling along it */
  .au-card-strip {
    position: absolute;
    top: 0; left: 0; right: 0;
    height: 4px;
    background: linear-gradient(90deg,
      var(--u-from, #ea580c), var(--u-to, #f59e0b), var(--u-from, #ea580c));
    background-size: 220% 100%;
    animation: auStrip 3.4s linear infinite;
  }
  @keyframes auStrip {
    to { background-position: 220% 0; }
  }
  .au-card:hover {
    transform: translateY(-7px);
    box-shadow: 0 24px 44px color-mix(in srgb, var(--u-accent, #ea580c) 28%, transparent) !important;
  }
  @supports not (box-shadow: 0 0 0 color-mix(in srgb, red 10%, blue)) {
    .au-card:hover { box-shadow: 0 22px 40px rgba(194, 65, 12, 0.2) !important; }
  }
  /* Admins get a permanent brass rim */
  .au-card.is-admin {
    border-color: rgba(251, 191, 36, 0.65) !important;
    background: linear-gradient(160deg, #ffffff, #fffaf0);
  }

  /* ===== Avatar ===== */
  .au-avatar-wrap { position: relative; flex-shrink: 0; line-height: 0; }
  .au-avatar {
    position: relative;
    z-index: 1;
    width: 56px;
    height: 56px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    color: #fff;
    font-weight: 800;
    font-size: 1.4rem;
    background: linear-gradient(135deg, var(--u-from, #ea580c), var(--u-to, #f59e0b));
    box-shadow: 0 6px 16px color-mix(in srgb, var(--u-accent, #ea580c) 45%, transparent);
    transition: transform 0.35s cubic-bezier(0.34, 1.56, 0.64, 1);
  }
  @supports not (box-shadow: 0 0 0 color-mix(in srgb, red 10%, blue)) {
    .au-avatar { box-shadow: 0 6px 16px rgba(124, 45, 18, 0.3); }
  }
  .au-card:hover .au-avatar { transform: scale(1.09) rotate(-5deg); }

  /* Turning halo — admins only */
  .au-halo {
    position: absolute;
    inset: -6px;
    z-index: 0;
    border-radius: 50%;
    background: conic-gradient(from 0deg,
      #fde68a, #f59e0b, #fff7ed, #fbbf24, #fde68a);
    animation: auHalo 5s linear infinite;
  }
  @keyframes auHalo {
    to { transform: rotate(360deg); }
  }
  .au-crown {
    position: absolute;
    top: -12px;
    right: -8px;
    z-index: 2;
    font-size: 1.05rem;
    line-height: 1;
    filter: drop-shadow(0 3px 6px rgba(180, 83, 9, 0.5));
    animation: auCrown 3s ease-in-out infinite;
  }
  @keyframes auCrown {
    0%, 100% { transform: translateY(0) rotate(-12deg); }
    50%      { transform: translateY(-4px) rotate(8deg); }
  }

  .au-name { color: #1f2937; transition: color 0.25s ease; }
  .au-card:hover .au-name { color: var(--u-accent, #ea580c); }

  /* ===== Role pill ===== */
  .au-role {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    margin-top: 3px;
    padding: 3px 11px;
    border-radius: 30px;
    font-size: 0.72rem;
    font-weight: 700;
    border: 1px solid transparent;
  }
  .au-role-dot {
    width: 6px; height: 6px;
    border-radius: 50%;
    background: currentColor;
  }
  .au-role.is-admin {
    color: #b45309;
    background: #fffbeb;
    border-color: #fcd34d;
  }
  .au-role.is-admin .au-role-dot { animation: auDot 1.7s ease-in-out infinite; }
  @keyframes auDot {
    0%, 100% { opacity: 0.4; transform: scale(0.8); }
    50%      { opacity: 1;   transform: scale(1.3); }
  }
  .au-role.is-user {
    color: #6d28d9;
    background: #f5f3ff;
    border-color: #ddd6fe;
  }

  /* ===== Info rows ===== */
  .au-info { font-size: 0.88rem; color: #475569; }
  .au-info-row {
    display: flex;
    align-items: center;
    gap: 9px;
    padding: 6px 10px;
    border-radius: 10px;
    transition: background 0.25s ease, transform 0.25s ease;
  }
  .au-info-row:hover {
    background: #fff7ed;
    transform: translateX(3px);
  }
  .au-info-icon {
    flex: none;
    width: 26px; height: 26px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 8px;
    font-size: 0.8rem;
    background: linear-gradient(135deg, #fff7ed, #fde68a);
  }

  /* ===== Actions ===== */
  .au-act {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 7px;
    border-radius: 12px;
    padding: 8px 14px;
    font-size: 0.82rem;
    font-weight: 700;
    cursor: pointer;
    background: #fff;
    transition: transform 0.25s cubic-bezier(0.34, 1.56, 0.64, 1),
      box-shadow 0.25s ease, background 0.25s ease, color 0.25s ease,
      border-color 0.25s ease;
  }
  .au-act:hover { transform: translateY(-2px); }
  .au-act:active { transform: translateY(0) scale(0.96); }
  .au-act-ico { display: inline-block; }

  .au-act-promote {
    border: 1px solid #fcd34d;
    color: #b45309;
  }
  .au-act-promote:hover {
    color: #431407;
    border-color: transparent;
    background: linear-gradient(135deg, #fde68a, #fbbf24 55%, #f59e0b);
    box-shadow: 0 10px 22px rgba(245, 158, 11, 0.45);
  }
  .au-act-promote:hover .au-act-ico {
    animation: auStarSpin 1.4s ease-in-out infinite;
  }
  @keyframes auStarSpin {
    0%, 100% { transform: rotate(0deg) scale(1); }
    50%      { transform: rotate(180deg) scale(1.25); }
  }

  .au-act-del {
    border: 1px solid #fecaca;
    color: #b91c1c;
  }
  .au-act-del:hover {
    color: #fff;
    border-color: transparent;
    background: linear-gradient(135deg, #991b1b, #dc2626 55%, #ef4444);
    box-shadow: 0 10px 22px rgba(220, 38, 38, 0.42);
  }
  .au-act-del:hover .au-act-ico {
    animation: auShake 0.55s ease-in-out infinite;
  }
  @keyframes auShake {
    0%, 100% { transform: translateX(0) rotate(0deg); }
    30%      { transform: translateX(-2px) rotate(-14deg); }
    65%      { transform: translateX(2px) rotate(11deg); }
  }

  .au-empty-icon {
    font-size: 3rem;
    animation: auIconBob 3s ease-in-out infinite;
  }

  @media (prefers-reduced-motion: reduce) {
    .au-hero,
    .au-hero::before,
    .au-hero::after,
    .au-hero-aurora,
    .au-mote,
    .au-hero-icon,
    .au-hero-title-text,
    .au-chip.is-active,
    .au-card,
    .au-card-strip,
    .au-halo,
    .au-crown,
    .au-role.is-admin .au-role-dot,
    .au-act-promote:hover .au-act-ico,
    .au-act-del:hover .au-act-ico,
    .au-empty-icon { animation: none !important; }
    .au-motes { display: none; }
    .au-hero-title-text {
      -webkit-text-fill-color: #fff;
      color: #fff;
    }
  }
`;

export default AdminUsersPage;
